"use client";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";

import PharmacistSignatureModal from "./PharmacistSignatureModal";
import ActionTile from "../../Dispensary/Chronic/components/ActionTile";
import DeliveryOptions from "../../Dispensary/Chronic/components/DeliveryOptions";
import FeeSummaryCard from "../../Dispensary/Chronic/components/FeeSummaryCard";
import ReadOnlyInput from "../../../components/ReadOnlyInput";
import PaymentField from "../../../components/PaymentField";
import styles from "../../Dispensary/Chronic/css/ChronicScriptView.module.css";
import Status from "../../../components/Status";
import localStyles from "../css/ClaimApprovedTable.module.css";
export default function ScriptHistoryDetailsView({ scriptData: scriptDataProp, returnToPath: returnToPathProp, onInPlaceContinue, embed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locState = location.state || {};
  const scriptData = scriptDataProp ?? locState.scriptData;
  const returnToPath = returnToPathProp ?? locState.returnToPath;

  const [currentPage, setCurrentPage] = useState(1);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState("Collect");
  const rowsPerPage = 2;

  // Example fallback values
  const authorisationNumber = scriptData?.authorisationNumber || "AUTH-001";
  const pharmacist = scriptData?.pharmacist || "Dr. Jane Doe";
  const paymentType = scriptData?.paymentType || "Medical Aid";
  const switchRef = scriptData?.switchReference || "SW-123456";
  const processed = scriptData?.processed || "2025/07/11 15:19:39";
  const totalItems = scriptData?.drugs?.length || 3;

  // Example table data
  const tableData = [
    {
      drugName: "Paracetamol",
      packSize: "100 tablets",
      icd10: "M54.9",
      strength: "500mg",
      qty: 50,
      nappiCode: "NAP123456",
      instructions: "Take 1 tablet every 6 hours",
      costPrice: 25.0,
      coPayment: "R10.00",
      status: "Approved",
      reason: "-",
    },
    {
      drugName: "Ibuprofen",
      packSize: "50 tablets",
      icd10: "M79.1",
      strength: "400mg",
      qty: 30,
      nappiCode: "NAP654321",
      instructions: "Take with food",
      costPrice: 30.0,
      coPayment: "R15.00",
      status: "Partially Approved",
      reason: "Limit reached",
    },
    {
      drugName: "Aspirin",
      packSize: "60 tablets",
      icd10: "I10",
      strength: "300mg",
      qty: 20,
      nappiCode: "NAP789123",
      instructions: "Take daily",
      costPrice: 20.0,
      coPayment: "R0.00",
      status: "Rejected",
      reason: "Not covered",
    },
  ];

  // Pagination
  const totalPages = Math.ceil(tableData.length / rowsPerPage);
  const paginatedData = tableData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Table columns
  const columns = [
    { header: "Drug Name", accessor: "drugName" },
    { header: "Pack Size", accessor: "packSize" },
    { header: "ICD-10", accessor: "icd10" },
    { header: "Strength", accessor: "strength" },
    { header: "Qty", accessor: "qty" },
    { header: "Nappi Code", accessor: "nappiCode" },
    { header: "Instructions", accessor: "instructions" },
    { header: "Cost Price", accessor: "costPrice" },
    { header: "Co-Payments", accessor: "coPayment" },
    { header: "Status", accessor: "status", render: (value) => <Status status={value} /> },
    { header: "Reason", accessor: "reason" },
  ];

  // Example fee breakdown
  const paymentDetails = {
    priceTitle: "Price",
    price: "R75.00",
    feeTitle: "Professional Fee",
    fee: "R15.00",
    coPayTitle: "Co-Payment",
    coPay: "R25.00",
    grossTitle: "Gross",
    gross: "R115.00",
  };

  const savingsAndMrpDetails = {
    savingsTitle: "Savings Amount",
    savingsValue: "R20.00",
    mrpTitle: "MRP Amount",
    mrp: "R150.00",
  };

  // Handlers
  const handleSignInvoice = () => setShowSignatureModal(true);
  const handleSignatureNext = (signatureData) => {
    console.log("Signature saved:", signatureData);
    setShowSignatureModal(false);
    if (typeof onInPlaceContinue === 'function') {
      onInPlaceContinue(signatureData);
      return;
    }
    if (returnToPath) {
      navigate(returnToPath, {
        state: { scriptData, continueAfterMedicalAid: true, pharmacistSignature: signatureData },
      });
    }
  };

  const handleMedicationLabel = () => console.log("Generate Medication Label");
  const handleDeliveryLabel = () => console.log("Generate Delivery Label");
  const handlePackageAndLabel = () => console.log("Generate Package and Label");

  // DeliveryOptions component provides constant choices (Collect/Delivery)

  return (
    <div className={styles.page}>
      {!embed && <Navbar />}

      <main className={`${styles.main} ${localStyles.pageMain}`}>
        {/* Top controls */}
        <div className={localStyles.controlsWrap}>
          <div className={localStyles.controlsRow}>
            <ReadOnlyInput label="Authorization Number" value={authorisationNumber} />
            <ReadOnlyInput label="Pharmacist" value={pharmacist} />
            <ReadOnlyInput label="Payment Type" value={paymentType} />
            <ReadOnlyInput label="Switch Reference" value={switchRef} />
            <ReadOnlyInput label="Processed" value={processed} />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableCard}>
          <Table columns={columns} data={paginatedData} />
        </div>

        <div className={localStyles.claimActionsRow}>
          <div className={localStyles.statusScaled} style={{ marginLeft: '190px' }}>
            <Status status="Claim Approved" />
          </div>
          <div className={`${styles.tilesMiddle} ${localStyles.tilesMiddleFlex}`}>

          </div>
        </div>

        {/* Mid row: tighter spacing */}
        <div className={`${styles.midRow} ${localStyles.midRowTight}`}>
          <div className={styles.feesLeft}>
            <div className={localStyles.mb16}>
              <PaymentField details={savingsAndMrpDetails} />
            </div>
            <div className={localStyles.mb16}>
              <PaymentField details={paymentDetails} />
            </div>
          </div>
          <div className={styles.tilesMiddle}>
            <div className={styles.tilesCol}>
              <ActionTile title="Sign Invoice" onClick={handleSignInvoice} />
              <div className={localStyles.labelButtonsRow}>
                <button className={styles.btnPrimary} onClick={handleMedicationLabel}>Medication Label</button>
                <button className={styles.btnPrimary} onClick={handleDeliveryLabel}>Delivery Label</button>
              </div>
            </div>
          </div>
          <div className={`${styles.optionsRight} ${localStyles.optionsRightTight}`}>
            <DeliveryOptions mode={deliveryOption} onModeChange={setDeliveryOption} />
          </div>
        </div>

        {/* Bottom buttons removed; moved under Sign Invoice to tidy layout */}

        {totalPages > 1 && (
          <div className={localStyles.paginationWrap}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </main>

      {!embed && <Footer />}

      <PharmacistSignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onNext={handleSignatureNext}
      />
    </div>
  );
}